import React, { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "../../utilities/lazyWithRetry";
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import "./App.css";
import Loader from "../reusableComponents/Loader/Loader";
import { apiRequest, getUserDetails } from "../../services/apiRequest";
import { hasCmsAccess } from "../../utilities/permissions";
import ErrorMessage from "../reusableComponents/ErrorMessage/ErrorMessage";
import OfflineToast from "../reusableComponents/OfflineToast/OfflineToast";
import { useDispatch, useSelector } from "react-redux";
import {
    addToErrorMessage,
    selectErrorMessage,
} from "../../features/errorMessage/errorMessageSlice";
import { addToLoader, selectLoader } from "../../features/loader/loaderSlice";

import { requestType } from "../../services/requests";
import {
    selectRoyalInternationalPopup,
    selectShowStateDetailsConfirmation,
} from "../../features/detailsConfirmation/detailsConfirmationSlice";
import {
    selectCountryCode,
    selectShowStateCountrySelector,
} from "../../features/countrySelector/countrySelectorSlice";
import {
    checkCurrentLanguage,
    handlePathsBasedOnLanguage,
    redirectToComWithEsPath,
} from "../../utilities/helper";

import TuiBeforeStart from "../overlay/TuiBeforeStart/TuiBeforeStart";
import RoyalInternationalPopup from "../overlay/RoyalInternationalPopup/RoyalInternationalPopup";
import Overview from "../CustomerOuput/Overview/Overview";
import RebrandingPopup from "../overlay/RebrandingPopup/RebrandingPopup.jsx";
import LegacyMomentumResetPasswordRedirect from "../RoyalMomentum/ResetPassword/LegacyResetPasswordRedirect";
import { selectRebrandingPopupState, updateRebrandingPopup } from "../../features/likedCruises/likedCruisesSlice.js";
// Bundled with the entry rather than lazy-loaded: "/" renders it, so a failed
// fetch of its chunk on first load locked the user out of the whole app
// (support task 41863718, Sentry MY-ROYAL-GENIE-FRONTEND-7F).
import LoginNew from "../Auth/Login/LoginNew";

// Dynamically import components with React.lazy, retrying a failed chunk fetch
const RegisterNew = lazyWithRetry(() => import("../Auth/Register/RegisterNew"));
const ResetPassword = lazyWithRetry(() => import("../Auth/ResetPassword/ResetPassword"));
const ResetPasswordNew = lazyWithRetry(() => import("../Auth/ResetPassword/ResetPasswordNew"));
const WhatIncluded = lazyWithRetry(() => import("../CustomerOuput/WhatIncluded/WhatIncluded"));
const WhatIncludedNew = lazyWithRetry(() => import("../CustomerOuput/WhatIncluded/WhatIncludedNew"));
const SuiteLife = lazyWithRetry(() => import("../CustomerOuput/SuiteLife/SuiteLife"));
const Accessibility = lazyWithRetry(() => import("../CustomerOuput/Accessibility/Accessibility"));
const Extras = lazyWithRetry(() => import("../CustomerOuput/Extras/Extras"));
// const Overview = lazyWithRetry(() => import("../CustomerOuput/Overview/Overview"));
const Homepage = lazyWithRetry(() => import("../Homepage/Homepage"));
const Itinerary = lazyWithRetry(() => import("../IndividualIntinerary/Itinerary/Itinerary"));
const ItineraryShip = lazyWithRetry(() => import("../IndividualIntinerary/ItineraryShip/ItineraryShip"));
const ItineraryDay = lazyWithRetry(() => import("../IndividualIntinerary/ItineraryDay/ItineraryDay"));
const BuildMyHoliday = lazyWithRetry(() => import("../BuildMyHoliday/BuildMyHoliday"));
const CreateYourHoliday = lazyWithRetry(() => import("../CreateYourHoliday/CreateYourHoliday"));
const HolidaySuitcase = lazyWithRetry(() => import("../HolidaySuitcase/HolidaySuitcase"));
const Pitch = lazyWithRetry(() => import("../Pitch/Pitch"));
const HolidaysDashboard = lazyWithRetry(() => import("../HolidaysDashboard/HolidaysDashboard"));
const ClosingLounge = lazyWithRetry(() => import("../HolidaysDashboard/ClosingLounge"));
const ClientSuitcase = lazyWithRetry(() => import("../HolidaysDashboard/ClientSuitcase"));
const ClosingChat = lazyWithRetry(() => import("../HolidaysDashboard/ClosingChat"));
const AiChat = lazyWithRetry(() => import("../HolidaysDashboard/AIChat"));
const Account = lazyWithRetry(() => import("../Account/Account"));
const HowItWorks = lazyWithRetry(() => import("../HowItWorks/HowItWorks"));
const CMSLogin = lazyWithRetry(() => import("../CMS/Login/CMSLogin"));
const CMSHomepage = lazyWithRetry(() => import("../CMS/Homepage/CMSHomepage"));
const CMSItineraries = lazyWithRetry(() => import("../CMS/Itineraries/CMSItineraries"));
const CMSUploadLatestCampaign = lazyWithRetry(() =>
    import("../CMS/UploadLatestCampaign/CMSUploadLatestCampaign")
);
const CMSCampaignUploadPage = lazyWithRetry(() =>
    import("../CMS/UploadLatestCampaign/CMSCampaignUploadPage")
);
const CMSFocusItinerary = lazyWithRetry(() => import("../CMS/FocusItinerary/CMSFocusItinerary"));
const CMSAnalytics = lazyWithRetry(() => import("../CMS/Analytics/CMSAnalytics"));
const CMSItineraryStatus = lazyWithRetry(() => import("../CMS/ItineraryStatus/CMSItineraryStatus"));
const CMSUserManagement = lazyWithRetry(() => import("../CMS/UserManagement/CMSUserManagement"));
const CMSAddFocusItinerary = lazyWithRetry(() =>
    import("../CMS/FocusItinerary/AddFocusItinerary/CMSAddFocusItinerary")
);
const ItinerarySeaDay = lazyWithRetry(() =>
    // import("../IndividualIntinerary/ItinerarySeaDay/ItinerarySeaDay")
    import("../IndividualIntinerary/ItinerarySeaDay/ItinerarySeaDayV2")
);
const YourCruise = lazyWithRetry(() => import("../CustomerOuput/YourCruise/YourCruise"));
const WhyRoyal = lazyWithRetry(() => import("../CustomerOuput/WhyRoyal/WhyRoyalNew"));
const Ship = lazyWithRetry(() => import("../IndividualIntinerary/EnhancedTemplate/Ship"));
const Destination = lazyWithRetry(() => import("../IndividualIntinerary/EnhancedTemplate/Destination"));
const Contact = lazyWithRetry(() => import("../Contact/Contact"));
const AcceptableGenieAi = lazyWithRetry(() => import("../AcceptableGenieAi/AcceptableGenieAi.jsx"));
const TermsConditions = lazyWithRetry(() => import("../TermsConditions/TermsConditions"));
const TermsConditionsNordics = lazyWithRetry(() => import("../TermsConditions/TermsConditionsNordics"));
const TermsConditionsMalta = lazyWithRetry(() => import("../TermsConditions/TermsConditionsMalta"));
const ContactUs = lazyWithRetry(() => import("../ContactUs/ContactUs"));
const ErrorPage = lazyWithRetry(() => import("../ErrorPage/ErrorPage"));
// const TuiBeforeStart = lazyWithRetry(() => import("../overlay/TuiBeforeStart/TuiBeforeStart"));
const CountrySelectorNew = lazyWithRetry(() => import("../overlay/CountrySelector/CountrySelectorNew"));
const CMSLeaderboard = lazyWithRetry(() => import("../CMS/Leaderboard/CMSLeaderboard"));
const GetStartedLanding = lazyWithRetry(() => import("../GetStarted/GetStartedLanding/GetStartedLanding"));
const RoyalBridge = lazyWithRetry(() => import("../RoyalBridge/RoyalBridge"));
const RoyalMomentum = lazyWithRetry(() => import("../RoyalMomentum/RoyalMomentum"));
const AccessDenied = lazyWithRetry(() => import("../AccessDenied/AccessDenied"));
const RoyalMomentumResetPassword = lazyWithRetry(() =>
    import("../RoyalMomentum/ResetPassword/RoyalMomentumResetPassword")
);
const MeetingAgendaPublic = lazyWithRetry(() =>
    import("../RoyalMomentum/RoyalMomentumMeetingHub/pages/MeetingAgendaPublic/MeetingAgendaPublic")
);
const StoreVisitSummaryPublic = lazyWithRetry(() =>
    import("../RoyalMomentum/RoyalMomentumActivation/pages/StoreVisitSummaryPublic")
);
const KamVisitSummaryPublic = lazyWithRetry(() =>
    import("../RoyalMomentum/RoyalMomentumActivationKAM/pages/RoyalMomentumKamVisitSummaryPublic")
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Routes that must not render at all without a session.
//
// The old gate was a post-render `useEffect` that redirected only `/home*` and
// `/cms*`, so every other authenticated screen — /holidays-dashboard, /pitch,
// /account, /get-started, /closing-lounge, /client-suitcase and their /es twins —
// mounted for an anonymous visitor and issued session-less API calls before
// anything stopped it. This runs during render instead, so a blocked route never
// mounts. (RoyalBridge.jsx already did it this way; this brings MRG in line.)
//
// Deliberately an allow-by-default list: an unrecognised path stays reachable.
// Getting a path wrong here locks real agents out, which is worse than the hole
// being closed, so the list names only screens confirmed to render data or
// perform writes.
//
// NOT listed, on purpose:
//   * /contact-us, /terms-and-conditions* — reusableComponents/Footer links both
//     and that footer renders on the public customer microsites, so gating them
//     would bounce a customer to the agent login.
//   * /how-it-works, /acceptable-genie-ai — static informational pages. Gating
//     them buys no security and risks breaking a link.
//   * microsites (/:page/:type_share/:unique_code), /legend, /meeting-hub,
//     /store-visit-summary-public, /kam-visit-summary-public — no-session by design.
//   * /royal-bridge/*, /royal-momentum/* — each gates itself on mount.
const SESSION_REQUIRED_PATHS = [
    "/home",
    "/homepage",
    "/get-started",
    "/create-your-holiday",
    "/holiday-suitcase",
    "/pitch",
    "/holidays-dashboard",
    "/closing-lounge",
    "/client-suitcase",
    "/closing-chat",
    "/ai-chat",
    "/account",
    "/internal/user-management",
];

// Segment-exact, not `includes()`: a substring test would let any URL merely
// containing one of these strings match, and would make "/homepage" a child of
// "/home". Both language variants end in `/es`, which the prefix test covers.
//
// Lower-cased because React Router matches routes case-insensitively by default,
// so `/Holidays-Dashboard` renders the same screen as `/holidays-dashboard`. A
// case-sensitive comparison here would have been a one-keystroke bypass.
const matchesPath = (pathname, base) => {
    const path = pathname.toLowerCase();
    return path === base || path.startsWith(`${base}/`);
};

// Where to send an anonymous visitor, or null to let the route render.
export const anonymousRedirectFor = (pathname) => {
    // The CMS has its own login screen, so an anonymous visitor belongs there rather
    // than on the agent login.
    //
    // `/cms` is matched exactly, not as a prefix: treating it as a prefix made every
    // `/cms/*` admin screen look like the public login screen and left the whole CMS
    // open. The reset path IS a prefix — the emailed link carries a token and id.
    if (pathname.toLowerCase() === "/cms") return null;
    if (matchesPath(pathname, "/cms/reset-password")) return null;
    if (matchesPath(pathname, "/cms")) return "/cms";

    if (SESSION_REQUIRED_PATHS.some((base) => matchesPath(pathname, base))) return "/";
    return null;
};

function App() {
    const [firstLoading, setFirstLoading] = useState(true);
    const errorMessage = useSelector(selectErrorMessage);
    const loader = useSelector(selectLoader);
    const [newTitle, setNewTitle] = useState("");
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const showDetailsConfirmation = useSelector(selectShowStateDetailsConfirmation);
    const showHideRoyalInternationalPopup = useSelector(selectRoyalInternationalPopup);
    const rebrandingPopupStatus = useSelector(selectRebrandingPopupState);
    const showCountrySelector = useSelector(selectShowStateCountrySelector);
    const [userDetailsUpdated, setUserDetailsUpdated] = useState("");
    const countryCode = useSelector(selectCountryCode);

    const handleRebrandingPopupStatus = async (trackingType) => {
            const userDetails = getUserDetails();
            if (!userDetails) return;
            let payload = {
                user_uid: userDetails.user_details.uid,
                data_key: "mrg_20",
            };
            let request_type =
                trackingType === "setTracking"
                    ? requestType.setUserTracking
                    : requestType.getUserTracking;
            dispatch(addToLoader(true));
            const response = await apiRequest({
                payload: {
                    data: payload,
                },
                requestType: request_type,
            });
    
            if (trackingType === "setTracking") {
                if (response?.data?.status === 1) {
                    dispatch(addToErrorMessage(response?.data?.error_message));
                    dispatch(updateRebrandingPopup(false));
                } else {
                    dispatch(updateRebrandingPopup(false));
                }
            } 
            dispatch(addToLoader(false));
        };

    const checkIsScreenMicrosite = () => {
        const screens = ["preview", "public", "meeting-hub", "legend"];
        let isValid = 0;

        screens.forEach((item) => {
            if (window.location.pathname.includes(item)) {
                isValid++;
            }
        });

        if (isValid) return true;
        return false;
    };

    useEffect(() => {
        //Catch the current language
        //redirectToComWithEsPath(); //if runs this, checkCurrentLanguage must not run
        checkCurrentLanguage(location);
        // End ./ Catch the current language
        // }, [window.location.hostname]);

        //Rebranding Popup
        // handleRebrandingPopupStatus();
    }, []);

    useEffect(() => {
        //Handle Paths base on current language
        handlePathsBasedOnLanguage(location, navigate, function () {});
        // End ./ Handle Paths base on current language

        window.scrollTo(0, 0);

        //We need this for analytics, catching which page user is active and how long
        if (location.pathname === "/" || location.pathname === "/es") {
            document.title = "Royal Caribbean Personalised Brochure";
        } else {
            document.title = location.pathname
                .replaceAll("-", " ")
                .replace("/", "")
                .split("/")[0]
                .toUpperCase();
        }
        //End./ We need this for analytics, catching which page user is active and how long

        const userDetails = getUserDetails();
        setUserDetailsUpdated(userDetails);

        const checkUserSession = async () => {
            // An invalid session is handled centrally in apiRequest (it destroys
            // the stored user and hard-redirects to "/"). The old
            // `response.status === 1` check compared the HTTP status — never 1 —
            // so no per-response handling is needed here; what matters is that
            // firstLoading always clears, or the full-screen loader stays up
            // forever on a failed/timed-out request.
            try {
                await apiRequest({
                    payload: {
                        data: {
                            user_uid: userDetails.user_details.uid,
                        },
                    },
                    requestType: requestType.checkUserSession,
                });
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setFirstLoading(false);
            }
        };

        let currentLanguage = localStorage.getItem("mrgCurrentLanguage");

        if (!userDetails) {
            // Redirecting anonymous visitors is handled by anonymousRedirectFor during
            // render — before the route mounts — so there is nothing to do here but let
            // the app render the public route it was asked for.
            setFirstLoading(false);
        } else {
            // Auth pages a signed-in user has no reason to be on. Matching is exact, so
            // `/reset-password/:token/:id` is deliberately not caught — someone already
            // signed in must still be able to finish an emailed reset link.
            if (
                [
                    "/register",
                    "/reset-password",
                    `/register/${currentLanguage}`,
                    `/reset-password/${currentLanguage}`,
                ].includes(location.pathname)
            ) {
                navigate("/");
            } else {
                if (checkIsScreenMicrosite()) {
                    setFirstLoading(false);
                } else {
                    checkUserSession();
                }
                if (location.pathname.includes("/cms")) {
                    // Bitwise test on the CMS_ADMIN bit, not an equality test on the whole mask:
                    // the old `permissions !== 33` locked out any CMS Admin who also held another
                    // bit (e.g. User Management Admin → 1|32|64 = 97).
                    if (!hasCmsAccess(userDetails)) {
                        navigate("/");
                    }
                }
                
                //we need this to check if user has access on Royal Bridge, Royal Momentum or My Royal Genie
                const mrgPaths = [
                    "/home", "/homepage", "/get-started", "/create-your-holiday",
                    "/holiday-suitcase", "/pitch", "/holidays-dashboard",
                    "/closing-lounge", "/client-suitcase", "/closing-chat",
                    "/ai-chat", "/account", "/how-it-works", "/contact-us",
                    "/acceptable-genie-ai",
                ];
                const routeGuards = [
                    { label: "Royal Bridge", match: (p) => p.startsWith("/royal-bridge"), check: () => userDetails.user_details.is_royal_bridge_user },
                    { label: "Royal Momentum", match: (p) => p.startsWith("/royal-momentum"), check: () => userDetails.user_details.is_royal_pro_user },
                    { label: "My Royal Genie", match: (p) => mrgPaths.some((mp) => p.startsWith(mp)), check: () => userDetails.user_details.is_mrg_user },
                ];

                const blockedRoute = routeGuards.find(
                    (guard) => guard.match(location.pathname) && !guard.check()
                );
                
                if (blockedRoute) {
                    navigate("/access-denied", {
                        state: {
                            blockedPlatform: blockedRoute.label,
                            hasRoyalBridge: !!userDetails.user_details.is_royal_bridge_user,
                            hasRoyalMomentum: !!userDetails.user_details.is_royal_pro_user,
                            hasMrg: !!userDetails.user_details.is_mrg_user,
                        },
                    });
                }
            }
        }
    }, [location.pathname]);

    //we need this to force remove genie ai chat from dom if is not "/ai-chat" or "/closing-chat"
    useEffect(() => {
        const isAllowedPage =
            location.pathname.includes("/ai-chat") || location.pathname.includes("/ai-chat-test") || location.pathname.includes("/closing-chat");

        if (isAllowedPage) {
            return;
        }

        const removeFlowiseChatbot = () => {
            document.querySelectorAll("flowise-fullchatbot").forEach((el) => el.remove());

            document
                .querySelectorAll('[id*="flowise"], [class*="flowise"]')
                .forEach((el) => el.remove());
        };

        // Remove immediately
        removeFlowiseChatbot();

        // Kill reinjections
        const observer = new MutationObserver(removeFlowiseChatbot);

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, [location.pathname]);

    // Before the loader: an anonymous visitor on a protected route should go straight
    // to the login screen rather than watch a spinner for a page they cannot see.
    // Read straight from storage rather than from `userDetailsUpdated`, which is state
    // set by the effect above and is therefore a render behind on the first pass.
    const anonymousRedirect = getUserDetails() ? null : anonymousRedirectFor(location.pathname);
    if (anonymousRedirect) return <Navigate to={anonymousRedirect} replace />;

    if (firstLoading) return <Loader />;

    return (
        <div className="app">
            <div className="app__body">
                {errorMessage.data.length > 0 && (
                    <ErrorMessage
                        text={errorMessage.data}
                        onClick={() => dispatch(addToErrorMessage(""))}
                    />
                )}
                <OfflineToast />
                {loader.show && <Loader />}
                {showDetailsConfirmation && <TuiBeforeStart />}
                {showHideRoyalInternationalPopup && <RoyalInternationalPopup />}
                {/* {showCountrySelector && <CountrySelector />} */}
                {rebrandingPopupStatus && <RebrandingPopup handleRebrandingPopupStatus={() => {handleRebrandingPopupStatus('setTracking')}}/>}

                <Suspense fallback={<Loader />}>
                    <SentryRoutes>
                        <Route path="/" element={<LoginNew />} />
                        <Route path="/es" element={<LoginNew />} />

                        <Route path="/register" element={<RegisterNew />} />
                        <Route path="/register/es" element={<RegisterNew />} />

                        <Route path="/reset-password" element={<ResetPasswordNew />} />
                        <Route path="/reset-password/es" element={<ResetPasswordNew />} />

                        <Route path="/country-selector" element={<CountrySelectorNew />} />
                        <Route path="/country-selector/es" element={<CountrySelectorNew />} />

                        <Route path="/reset-password/:token/:id" element={<ResetPasswordNew />} />
                        <Route
                            path="/reset-password/:token/:id/es"
                            element={<ResetPasswordNew />}
                        />

                        <Route path="/homepage" element={<Homepage />} />
                        <Route path="/homepage/es" element={<Homepage />} />

                        <Route path="/home" element={<Homepage />} />
                        <Route path="/home/es" element={<Homepage />} />

                        {/* <Route path="/get-started" element={<BuildMyHoliday />} /> */}
                        <Route path="/get-started" element={<GetStartedLanding />} />
                        <Route path="/get-started/es" element={<GetStartedLanding />} />

                        {/* 3 Step */}
                        <Route path="/get-started/:step" element={<BuildMyHoliday />} />
                        <Route path="/get-started/:step/es" element={<BuildMyHoliday />} />

                        {/* 5 Step */}
                        <Route path="/create-your-holiday" element={<CreateYourHoliday />} />
                        <Route path="/create-your-holiday/:step" element={<CreateYourHoliday />} />
                        <Route
                            path="/create-your-holiday/:step/es"
                            element={<CreateYourHoliday />}
                        />

                        <Route path="/holiday-suitcase" element={<HolidaySuitcase />} />
                        <Route path="/holiday-suitcase/es" element={<HolidaySuitcase />} />

                        <Route path="/pitch" element={<Pitch />} />
                        <Route path="/pitch/es" element={<Pitch />} />

                        <Route path="/holidays-dashboard" element={<HolidaysDashboard />} />
                        <Route path="/holidays-dashboard/es" element={<HolidaysDashboard />} />

                        <Route path="/closing-lounge" element={<ClosingLounge />} />
                        <Route path="/closing-lounge/es" element={<ClosingLounge />} />

                        <Route path="/client-suitcase/:unique_code" element={<ClientSuitcase />} />
                        <Route
                            path="/client-suitcase/:unique_code/es"
                            element={<ClientSuitcase />}
                        />

                        <Route
                            path="/closing-chat/:unique_code/:itinerary_uid"
                            element={<ClosingChat />}
                        />
                        <Route
                            path="/closing-chat/:unique_code/:itinerary_uid/es"
                            element={<ClosingChat />}
                        />

                        <Route path="/ai-chat" element={<AiChat />} />
                        <Route path="/ai-chat/es" element={<AiChat />} />

                        <Route path="/account" element={<Account />} />
                        <Route path="/account/es" element={<Account />} />

                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/how-it-works/es" element={<HowItWorks />} />

                        {/* {userDetailsUpdated?.user_details?.country == "NORDICS" ? (
                        <Route path="/terms-and-conditions" element={<TermsConditionsNordics />} />
                    ) : (
                        <Route path="/terms-and-conditions" element={<TermsConditions />} />
                    )} */}
                        <Route path="/acceptable-genie-ai" element={<AcceptableGenieAi />} />
                        <Route path="/acceptable-genie-ai/es" element={<AcceptableGenieAi />} />
                        <Route path="/terms-and-conditions" element={<TermsConditions />} />
                        <Route path="/terms-and-conditions/es" element={<TermsConditions />} />
                        <Route
                            path="/terms-and-conditions/nordics"
                            element={<TermsConditionsNordics />}
                        />
                        <Route
                            path="/terms-and-conditions/malta"
                            element={<TermsConditionsMalta />}
                        />
                        <Route path="/terms-and-conditions/ae" element={<TermsConditionsMalta />} />

                        <Route path="/contact-us" element={<ContactUs />} />
                        <Route path="/contact-us/es" element={<ContactUs />} />

                        {/* Legend Trade */}
                        <Route
                            path="/overview/legend"
                            element={<Overview isLegend={true}/>}
                        />
                        <Route
                            path="/your-cruise/legend"
                            element={<YourCruise isLegend={true}/>}
                        />
                        <Route
                            path="/what-included/legend"
                            element={<WhatIncludedNew isLegend={true}/>}
                        />
                        <Route
                            path="/the-suite-life/legend"
                            element={<SuiteLife isLegend={true}/>}
                        />
                        <Route
                            path="/extras/legend"
                            element={<Extras isLegend={true}/>}
                        />
                        <Route
                            path="/contact/legend"
                            element={<Contact isLegend={true}/>}
                        />
                        <Route
                            path="/itinerary/:id/legend"
                            element={<Itinerary isLegend={true}/>}
                        />
                        <Route
                            path="/itinerary-ship/:id/legend"
                            element={<ItineraryShip isLegend={true}/>}
                        />
                        <Route
                            path="/enhanced-template-ship/:id/legend"
                            element={<Ship isLegend={true}/>}
                        />
                        <Route
                            path="/itinerary-day/:id/:itineraryUid/legend"
                            element={<ItineraryDay isLegend={true}/>}
                        />
                        <Route
                            path="/enhanced-template-destination/:id/:itineraryUid/legend"
                            element={<Destination isLegend={true}/>}
                        />
                        <Route
                            path="/itinerary-seaday/:id/:itineraryUid/legend"
                            element={<ItinerarySeaDay isLegend={true}/>}
                        />
                        {/* End./ Legend Trade */}

                        {/* <Route path="/overview" element={<Overview />} /> */}
                        <Route path="/overview/:type_share/:unique_code" element={<Overview />} />
                        <Route
                            path="/overview/:type_share/:unique_code/es"
                            element={<Overview />}
                        />

                        <Route
                            path="/your-cruise/:type_share/:unique_code"
                            element={<YourCruise />}
                        />
                        <Route
                            path="/your-cruise/:type_share/:unique_code/es"
                            element={<YourCruise />}
                        />

                        <Route path="/why-royal/:type_share/:unique_code" element={<WhyRoyal />} />
                        <Route
                            path="/why-royal/:type_share/:unique_code/es"
                            element={<WhyRoyal />}
                        />

                        <Route
                            path="/what-included/:type_share/:unique_code"
                            element={<WhatIncludedNew />}
                        />
                        <Route
                            path="/what-included/:type_share/:unique_code/es"
                            element={<WhatIncludedNew />}
                        />

                        <Route
                            path="/the-suite-life/:type_share/:unique_code"
                            element={<SuiteLife />}
                        />
                        <Route
                            path="/the-suite-life/:type_share/:unique_code/es"
                            element={<SuiteLife />}
                        />

                        <Route path="/extras/:type_share/:unique_code" element={<Extras />} />
                        <Route path="/extras/:type_share/:unique_code/es" element={<Extras />} />

                        <Route
                            path="/accessibility/:type_share/:unique_code"
                            element={<Accessibility />}
                        />
                        <Route
                            path="/accessibility/:type_share/:unique_code/es"
                            element={<Accessibility />}
                        />

                        <Route path="/contact/:type_share/:unique_code" element={<Contact />} />
                        <Route path="/contact/:type_share/:unique_code/es" element={<Contact />} />

                        <Route path="/itinerary/:id/:type" element={<Itinerary />} />
                        <Route path="/itinerary/:id/:type/es" element={<Itinerary />} />

                        <Route path="/itinerary-ship/:id/:type" element={<ItineraryShip />} />
                        <Route path="/itinerary-ship/:id/:type/es" element={<ItineraryShip />} />

                        <Route path="/enhanced-template-ship/:id/:type" element={<Ship />} />
                        <Route path="/enhanced-template-ship/:id/:type/es" element={<Ship />} />

                        <Route
                            path="/enhanced-template-destination/:id/:type"
                            element={<Destination />}
                        />
                        <Route
                            path="/enhanced-template-destination/:id/:type/es"
                            element={<Destination />}
                        />

                        <Route
                            path="/itinerary-day/:id/:itineraryUid/:type"
                            element={<ItineraryDay />}
                        />
                        <Route
                            path="/itinerary-day/:id/:itineraryUid/:type/es"
                            element={<ItineraryDay />}
                        />

                        <Route
                            path="/enhanced-template-destination/:id/:itineraryUid/:type"
                            element={<Destination />}
                        />
                        <Route
                            path="/enhanced-template-destination/:id/:itineraryUid/:type/es"
                            element={<Destination />}
                        />

                        <Route
                            path="/itinerary-seaday/:id/:itineraryUid/:type"
                            element={<ItinerarySeaDay />}
                        />
                        <Route
                            path="/itinerary-seaday/:id/:itineraryUid/:type/es"
                            element={<ItinerarySeaDay />}
                        />

                        <Route
                            path="/itinerary/:id/:type/:type_share/:unique_code"
                            element={<Itinerary />}
                        />
                        <Route
                            path="/itinerary/:id/:type/:type_share/:unique_code/es"
                            element={<Itinerary />}
                        />

                        <Route
                            path="/itinerary-ship/:id/:type/:type_share/:unique_code"
                            element={<ItineraryShip />}
                        />
                        <Route
                            path="/itinerary-ship/:id/:type/:type_share/:unique_code/es"
                            element={<ItineraryShip />}
                        />

                        <Route
                            path="/enhanced-template-ship/:id/:type/:type_share/:unique_code"
                            element={<Ship />}
                        />
                        <Route
                            path="/enhanced-template-ship/:id/:type/:type_share/:unique_code/es"
                            element={<Ship />}
                        />

                        <Route
                            path="/itinerary-day/:id/:itineraryUid/:type/:type_share/:unique_code"
                            element={<ItineraryDay />}
                        />
                        <Route
                            path="/itinerary-day/:id/:itineraryUid/:type/:type_share/:unique_code/es"
                            element={<ItineraryDay />}
                        />

                        <Route
                            path="/enhanced-template-destination/:id/:itineraryUid/:type/:type_share/:unique_code"
                            element={<Destination />}
                        />
                        <Route
                            path="/enhanced-template-destination/:id/:itineraryUid/:type/:type_share/:unique_code/es"
                            element={<Destination />}
                        />

                        <Route
                            path="/itinerary-seaday/:id/:itineraryUid/:type/:type_share/:unique_code"
                            element={<ItinerarySeaDay />}
                        />
                        <Route
                            path="/itinerary-seaday/:id/:itineraryUid/:type/:type_share/:unique_code/es"
                            element={<ItinerarySeaDay />}
                        />

                        {/* Public Meeting Agenda */}
                        <Route
                            path="/meeting-hub/:unique_code"
                            element={<MeetingAgendaPublic />}
                        />

                        {/* Public Store Visit Summary */}
                        <Route
                            path="/store-visit-summary-public"
                            element={<StoreVisitSummaryPublic />}
                        />

                        {/* Public KAM Visit Summary */}
                        <Route
                            path="/kam-visit-summary-public"
                            element={<KamVisitSummaryPublic />}
                        />

                        {/* Royal Bridge - nested routes */}
                        <Route path="/royal-bridge/*" element={<RoyalBridge />} />
                        <Route path="/royal-bridge/es/*" element={<RoyalBridge />} />

                        {/* Royal Momentum - nested routes */}
                        <Route path="/royal-momentum/*" element={<RoyalMomentum />} />
                        <Route path="/royal-momentum/es/*" element={<RoyalMomentum />} />

                        {/* Royal Momentum - password reset. Flat top-level path, outside the
                            nested tree above: the screen needs nothing from it and must not
                            meet its session gate. The old nested URL still redirects here
                            for links already emailed. */}
                        <Route
                            path="/royal-momentum-reset-password"
                            element={<RoyalMomentumResetPassword />}
                        />
                        <Route
                            path="/royal-momentum-reset-password/es"
                            element={<RoyalMomentumResetPassword />}
                        />
                        <Route
                            path="/royal-momentum-reset-password/:token/:id"
                            element={<RoyalMomentumResetPassword />}
                        />
                        <Route
                            path="/royal-momentum-reset-password/:token/:id/es"
                            element={<RoyalMomentumResetPassword />}
                        />
                        <Route
                            path="/royal-momentum/reset-password/*"
                            element={<LegacyMomentumResetPasswordRedirect />}
                        />

                        <Route path="/access-denied" element={<AccessDenied />} />

                        {/* Internal only - not linked in nav. Gated inside the component by the
                            "User Management Admin" (bit 64) permission, independent of the /cms
                            gate — see utilities/permissions.js for both. */}
                        <Route path="/internal/user-management" element={<CMSUserManagement />} />

                        <Route path="/errorpage" element={<ErrorPage />} />
                        <Route path="*" element={<ErrorPage />} />

                        {/* CMS */}
                        <Route path="/cms" element={<CMSLogin />} />
                        <Route path="/cms/reset-password" element={<ResetPassword />} />
                        <Route path="/cms/reset-password/:token/:id" element={<ResetPassword />} />
                        <Route path="/cms/home" element={<CMSHomepage />} />
                        <Route path="/cms/itineraries" element={<CMSItineraries />} />
                        <Route path="/cms/focus-itinerary" element={<CMSFocusItinerary />} />
                        <Route
                            path="/cms/focus-itinerary/form"
                            element={<CMSAddFocusItinerary />}
                        />
                        <Route
                            path="/cms/focus-itinerary/form/:id"
                            element={<CMSAddFocusItinerary />}
                        />
                        <Route path="/cms/analytics" element={<CMSAnalytics />} />
                        <Route path="/cms/itinerary-status" element={<CMSItineraryStatus />} />
                        <Route path="/cms/leaderboard" element={<CMSLeaderboard />} />
                        <Route
                            path="/cms/upload-latest-campaign"
                            element={<CMSUploadLatestCampaign />}
                        />
                        <Route
                            path="/cms/upload-latest-campaign/upload"
                            element={<CMSCampaignUploadPage />}
                        />
                        <Route path="/cms/account" element={<Account />} />
                    </SentryRoutes>
                </Suspense>
            </div>
        </div>
    );
}

export default App;
